/**
 * ThemeContext (iter74 + auto-mode enhancement)
 *
 * Tri-state theme:
 *   · "dark"  → Elite Performance (default)
 *   · "light" → Clean Professional
 *   · "auto"  → follows the OS's prefers-color-scheme, updates live if the
 *               user flips their system dark-mode toggle while the app is open.
 *
 * Choice is persisted in localStorage AND on the user profile via
 * POST /api/me/theme so it follows the user across devices.
 *
 * The DOM only sees the RESOLVED theme ("dark" or "light") applied via
 * `data-theme` — nothing else in the app has to know about "auto".
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ThemeCtx = createContext(null);
const STORAGE_KEY = "rally_theme";
const VALID = new Set(["dark", "light", "auto"]);
const DEFAULT_MODE = "dark";
const MEDIA_QUERY = "(prefers-color-scheme: dark)";

function readSystemPref() {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia(MEDIA_QUERY).matches ? "dark" : "light";
}

function resolveTheme(mode) {
  return mode === "auto" ? readSystemPref() : mode;
}

function loadInitial() {
  if (typeof window === "undefined") return DEFAULT_MODE;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return VALID.has(v) ? v : DEFAULT_MODE;
  } catch { return DEFAULT_MODE; }
}

function applyToDom(effective) {
  if (typeof document === "undefined") return;
  // Iter368 · Fix 2 — The external public wall-display viewer at
  // /festival/:id/viewer MUST always render in dark mode regardless
  // of the organiser's personal theme (this same viewer often runs
  // on the same authenticated device that's already got light mode
  // active). We short-circuit here so the ThemeProvider effect
  // stops fighting FestivalPublicViewer's child effect. Path check
  // uses window.location so it stays synchronous with each mount.
  // Iter370 · Fix 1 — Same dark-only lock applies to Live Ops
  // (`/festival/:id/live`) and Player Manager (`/festival/:id/players`);
  // both views were never designed around the light palette and
  // legacy hardcoded surfaces still bleed through. Any nested route
  // under those two also stays dark.
  const path = (typeof window !== "undefined" && window.location) ? window.location.pathname : "";
  const isPublicViewer = /^\/festival\/[^/]+\/viewer\/?$/.test(path);
  const isDarkOnlyOpsRoute = /^\/festival\/[^/]+\/(live|players)(\/|$)/.test(path);
  const applied = (isPublicViewer || isDarkOnlyOpsRoute) ? "dark" : effective;
  document.documentElement.setAttribute("data-theme", applied);
  document.documentElement.style.colorScheme = applied;
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [mode, setModeState] = useState(loadInitial);
  const [effective, setEffective] = useState(() => resolveTheme(loadInitial()));
  const hydratedFromUser = useRef(false);

  // Push effective theme to DOM whenever it changes.
  useEffect(() => { applyToDom(effective); }, [effective]);

  // Whenever `mode` changes, recompute effective.
  useEffect(() => { setEffective(resolveTheme(mode)); }, [mode]);

  // System-preference listener — only active while mode === "auto" so we
  // don't repaint dark-locked / light-locked users when they twiddle system
  // settings.
  useEffect(() => {
    if (mode !== "auto") return;
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(MEDIA_QUERY);
    const handler = () => setEffective(mq.matches ? "dark" : "light");
    // Modern spec: `addEventListener("change", …)`. Fall back to the legacy
    // Safari <14 API just in case (some kiosk devices lag behind).
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, [mode]);

  // Hydrate ONCE from the user profile (first time /auth/me lands).
  useEffect(() => {
    if (hydratedFromUser.current) return;
    const pref = user?.theme_preference;
    if (pref && VALID.has(pref)) {
      hydratedFromUser.current = true;
      setModeState(pref);
      try { window.localStorage.setItem(STORAGE_KEY, pref); } catch { /* ignore */ }
    } else if (user) {
      hydratedFromUser.current = true;
    }
  }, [user]);

  const setMode = useCallback(async (next) => {
    if (!VALID.has(next)) return;
    setModeState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    try { await api.post("/me/theme", { theme: next }); } catch { /* ignore */ }
  }, []);

  // Tri-state cycle: dark → light → auto → dark
  const cycle = useCallback(() => {
    const nextMap = { dark: "light", light: "auto", auto: "dark" };
    setMode(nextMap[mode] || "dark");
  }, [mode, setMode]);

  const value = useMemo(
    () => ({ mode, theme: effective, setMode, cycle, /* legacy alias */ setTheme: setMode, toggle: cycle }),
    [mode, effective, setMode, cycle],
  );
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) {
    return {
      mode: DEFAULT_MODE, theme: DEFAULT_MODE,
      setMode: () => {}, cycle: () => {}, setTheme: () => {}, toggle: () => {},
    };
  }
  return ctx;
}
